import { v4 as uuidv4 } from "uuid";
import timezoneFormats from "./timezone_date_formats.json";
import "dayjs/locale/en-in";
import dayjs from "dayjs";
import { MapLocationEvent, TraceItemV2, ProductInfo } from './types';
import { NOT_AVAILABLE } from "./constants";

export function generateSessionId() {
    return uuidv4();
}

export function storeSessionId(traceItemId: string, sessionId: string) {
    const now = new Date();
    const expiryTime = now.getTime() + 24 * 60 * 60 * 1000; // 24 hours
    // const expiryTime = now.getTime() + 3 * 1000 ; // 3 seconds for testing
    const item = {
        value: sessionId,
        expiry: expiryTime,
        feedbackSubmitted: false,
    };
    const sessionData = JSON.parse(localStorage.getItem("sessionData") || "{}");
    sessionData[traceItemId] = item;
    localStorage.setItem("sessionData", JSON.stringify(sessionData));
}

export function getSessionId(traceItemId: string) {
    const sessionData = JSON.parse(localStorage.getItem("sessionData") || "{}");
    const item = sessionData[traceItemId];
    if (!item) {
        return null;
    }
    const now = new Date();
    const currentTime = now.getTime();
    if (currentTime > item.expiry) {
        delete sessionData[traceItemId];
        localStorage.setItem("sessionData", JSON.stringify(sessionData));
        return null;
    }
    return item.value;
}

/**
 * Splits and groups traces based on their consecutive eventDefId.
 * 
 * @param traces - An array of trace objects to be processed.
 * @returns An array of objects where each object represents either a single trace or a group of consecutive traces 
 *          with the same eventDefId. Each object contains the type ('single' or 'group'), the data (trace or array of traces), 
 *          and the original index of the first trace in the input array.
 */
export function splitAndGroupTraces(traces: any[]) {
    const result: Array<{ type: 'single' | 'group', data: any | any[], originalIndex: number }> = [];
    let i = 0;

    while (i < traces.length) {
        const currentTrace = traces[i];
        const consecutiveGroup = [currentTrace];
        let j = i + 1;

        // Find consecutive traces with the same userEvent
        while (j < traces.length && traces[j].userEvent === currentTrace.userEvent) {
            consecutiveGroup.push(traces[j]);
            j++;
        }

        // If we found more than one consecutive trace with same userEvent, it's a group
        if (consecutiveGroup.length > 1) {
            result.push({
                type: 'group',
                data: consecutiveGroup,
                originalIndex: i
            });
        } else {
            // Single trace
            result.push({
                type: 'single',
                data: currentTrace,
                originalIndex: i
            });
        }

        i = j;
    }

    return result;
}

export const getFormattedUrl = (url: string) => {
    // If the URL doesn't already start with http:// or https://, prepend https://
    if (!/^https?:\/\//i.test(url)) {
        return `https://${url}`;
    }
    return url;
};

/**
 * Get user's current timezone
 */
export const getUserTimezone = () => Intl.DateTimeFormat().resolvedOptions().timeZone;
 
/** 
 * Display Selected Date as per user current time zone
*/
export const getDateFormatByTimezone = (isTimeRequired = false) => {
    const userTimezone = getUserTimezone();
    return `${timezoneFormats[userTimezone]} ${isTimeRequired ? "hh:mm A" : ""}` || "DD/MM/YYYY";
};

/**
 * Format epoch timestamp to readable date string in user's timezone
 */
export const formatEpochToDateTime = (epochValue) => {
    if (!epochValue) return "";
    const userTimezone = getUserTimezone();
    return dayjs.unix(Number(epochValue)).format(`${timezoneFormats[userTimezone]} hh:mm A`); 
};

/**
 * Groups traces for map View by coordinates
 */
export function groupTracesByLocation(traces: TraceItemV2[]): MapLocationEvent[] {
    const result: MapLocationEvent[] = [];
    const locationGroups = new Map<string, TraceItemV2[]>();
    
    // This is to handle slight variations in coordinates for events at the same location.(50 meters radius)
    const PROXIMITY_THRESHOLD = 0.00045; 
    
    traces.forEach(event => {
        const lat = event.latitude 
        const lng = event.longitude
        const locationName = event.location;
        
        // Filter out events without locationName, with "Not Available", or missing coordinates
        if(locationName === NOT_AVAILABLE || lat === undefined || lng === undefined){
            return;
        }
        
        // Create a coordinate key by rounding to nearby coordinates
        const latKey = Math.round(lat / PROXIMITY_THRESHOLD) * PROXIMITY_THRESHOLD;
        const lngKey = Math.round(lng / PROXIMITY_THRESHOLD) * PROXIMITY_THRESHOLD;
        const coordinateKey = `${latKey.toFixed(4)},${lngKey.toFixed(4)}`;
        
        if (!locationGroups.has(coordinateKey)) {
            locationGroups.set(coordinateKey, []);
        }
        locationGroups.get(coordinateKey)!.push(event);
    });

    // Create entries for each coordinate group
    locationGroups.forEach((eventsAtLocation) => {
        const firstEvent = eventsAtLocation[0];
        const lat = firstEvent.latitude
        const lng = firstEvent.longitude
        
        if (eventsAtLocation.length > 1) {
            // Multiple events at same coordinates
            result.push({
                eventId: firstEvent.eventID,
                event: firstEvent.eventDescription,
                location: firstEvent.location,
                latitude: lat!,
                longitude: lng!,
                eventStartUnixEpoch: firstEvent.eventStartUnixEpoch,
                groupedLocations: eventsAtLocation,
                primaryEvent: firstEvent,
                hasGroupedEvents: false,
                hasGroupedLocations: true,
                ...(firstEvent.freshnessMeterDetails && { freshnessMeterDetails: firstEvent.freshnessMeterDetails }),
            });
        } else {
            // Single event at unique coordinates
            result.push({
                eventId: firstEvent.eventID,
                event: firstEvent.eventDescription,
                location: firstEvent.location,
                latitude: lat!,
                longitude: lng!,
                eventStartUnixEpoch: firstEvent.eventStartUnixEpoch,
                groupedLocations: [],
                primaryEvent: firstEvent,
                hasGroupedEvents: false,
                hasGroupedLocations: false,
                ...(firstEvent.freshnessMeterDetails && { freshnessMeterDetails: firstEvent.freshnessMeterDetails }),
            });
        }
    });

    return result;
}

/**
 * Build images array from eventImages and productImages for a given eventId
 * @param eventId - The event ID
 * @param eventImages - Map of eventId to event image URL
 * @param productImages - Map of productInstanceId to product image URL
 * @param productInstanceIds - Optional array of productInstanceIds to look up product images
 */
export const getImagesForEvent = (
    eventId: string,
    eventImages: Record<string, string | null>,
    productImages: Record<string, string | null>,
    productInstanceIds?: string[]
): { src: string; title: string }[] => {
    const images: { src: string; title: string }[] = [];

    const eventImageUrl = eventImages[eventId];
    if (eventImageUrl) {
        images.push({ src: eventImageUrl, title: "Image from Event" });
    }

    // Look up product images by productInstanceId
    if (productInstanceIds && productInstanceIds.length > 0) {
        for (const instanceId of productInstanceIds) {
            const productImageUrl = productImages[instanceId];
            if (productImageUrl) {
                images.push({ src: productImageUrl, title: "Stock image" });
                break; // Only add first product image to avoid duplicates in carousel
            }
        }
    }

    return images;
};

/**
 * Parses outputProductInstances which can be either an array or a JSON string.
 * @param outputProductInstances - The value to parse (array or JSON string)
 * @returns Parsed string array
 */
export function parseOutputProductInstances(outputProductInstances: string[] | string | null | undefined): string[] {
    if (!outputProductInstances) return [];
    
    if (Array.isArray(outputProductInstances)) {
        return outputProductInstances;
    }
    
    if (typeof outputProductInstances === 'string') {
        try {
            return JSON.parse(outputProductInstances);
        } catch {
            return [];
        }
    }
    
    return [];
}


export const getProductNames = (
    isGrouped: boolean,
    groupedTraces: { eventID: string }[],
    eventId: string,
    productDetails: Record<string, ProductInfo[] | null>,
): string => {
    if (isGrouped) {
        // For grouped events, collect all products from all events
        const allProducts = groupedTraces.flatMap(
            (trace) => productDetails?.[trace.eventID] || [],
        );
        const uniqueNames = Array.from(
            new Set(allProducts?.map((p) => p.displayName).filter(Boolean)),
        );
        if (uniqueNames.length === 0) return "";
        if (uniqueNames.length === 1) return uniqueNames[0];
        return `${uniqueNames[0]}, +${uniqueNames.length - 1}`;
    }
    // For single events, get all products from that event
    const products = productDetails?.[eventId] || [];
    const uniqueNames = products?.map((p) => p.displayName).filter(Boolean);
    if (uniqueNames.length === 0) return "";
    if (uniqueNames.length === 1) return uniqueNames[0];
    return `${uniqueNames[0]}, +${uniqueNames.length - 1}`;
};

export const getTimeText = (
    isGrouped: boolean,
    firstItem: any,
    lastItem: any,
): string | null => {
    // Check if firstItem has valid eventTime
    if (!firstItem?.eventTime || firstItem.eventTime.trim() === "") {
        return null; // Return null to hide the entire time section
    }

    if (!isGrouped || !lastItem) {
        return firstItem.eventTime;
    }

    // Check if lastItem has valid eventTime for grouped events
    if (!lastItem?.eventTime || lastItem.eventTime.trim() === "") {
        return firstItem.eventTime;
    }

    // Parse event times to compare date and time
    const parseDateTime = (eventTime: string) => {
        if (!eventTime || typeof eventTime !== "string") {
            return { date: "", time: "" };
        }
        try {
            const parts = eventTime.trim().split(" ");
            if (parts.length === 0) {
                return { date: "", time: "" };
            }
            const datePart = parts[0]; // DD/MM/YYYY
            const timePart = parts.slice(1).join(" ").trim(); // HH:MM AM/PM
            return {
                date: datePart || "",
                time: timePart || "",
            };
        } catch (error) {
            return { date: "", time: "" };
        }
    };

    const firstDateTime = parseDateTime(firstItem?.eventTime || "");
    const lastDateTime = parseDateTime(lastItem?.eventTime || "");

    // Check if dates are the same
    if (firstDateTime.date && firstDateTime.date === lastDateTime.date) {
        // If times are also the same, show just one
        if (firstDateTime.time === lastDateTime.time) {
            return firstItem.eventTime;
        }
        // Same date, different times: show date-(time from to time)
        return `${firstDateTime.date} (${firstDateTime.time} - ${lastDateTime.time})`;
    }
    // Different dates or empty dates: show full range (if valid)
    if (firstDateTime.date && lastDateTime.date) {
        return `${firstItem.eventTime} -- ${lastItem.eventTime}`;
    }
    // Fallback to first item time if parsing failed
    return firstItem.eventTime;
};